import { useQuery } from '@tanstack/react-query';
import { useApi } from './client.js';

interface MeTenantsResponse {
  activeTenantId: string;
  email: string;
  /** CSAGE-439: session hierarchy scope + whether it is internal (root). */
  scopePath?: string | null;
  internal?: boolean;
  tenants: Array<{ tenantId: string; tenantName: string; roles: string[] }>;
}

/**
 * Roles the server resolved for the CURRENT session's active tenant.
 *
 * Reads GET /me/tenants, which derives roles from the verified session
 * identity (ctx.roles) — so it works for portal/HMAC sessions, not just
 * dev tokens. Prefer this over parseDevToken() for any role-gated UI,
 * because a real embedded portal token isn't a `dev:` string and
 * parseDevToken() returns null for it.
 */
export function useMyRoles(): { roles: string[]; isLoading: boolean } {
  const api = useApi();
  const q = useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: () => api.fetch<MeTenantsResponse>('/me/tenants'),
    staleTime: 5 * 60 * 1000,
  });
  const data = q.data;
  if (!data) return { roles: [], isLoading: q.isLoading };
  const active = data.tenants.find((t) => t.tenantId === data.activeTenantId) ?? data.tenants[0];
  return { roles: active?.roles ?? [], isLoading: false };
}

/**
 * CSAGE-556: which Insights sections the session may see. Mirrors the API's
 * read gates: the report routes accept the Report* roles, and the dashboard
 * routes (`DASHBOARD_READER_ROLES`) the Dashboard* roles; TenantAdmin and
 * CopilotUser reach both. With no roles known (still loading, or /me/tenants
 * failed) both stay visible, as before, so a slow or failed call never hides
 * Insights; the API still refuses what the session cannot read.
 */
const REPORT_SECTION_ROLES = [
  'ReportViewer',
  'ReportRunner',
  'ReportAuthor',
  'ReportAdmin',
  'TenantAdmin',
  'CopilotUser',
];
const DASHBOARD_SECTION_ROLES = [
  'DashboardViewer',
  'DashboardAuthor',
  'DashboardAdmin',
  'TenantAdmin',
  'CopilotUser',
];

export function insightsSections(roles: ReadonlyArray<string>): {
  reports: boolean;
  dashboards: boolean;
} {
  return {
    reports: roles.some((r) => REPORT_SECTION_ROLES.includes(r)),
    dashboards: roles.some((r) => DASHBOARD_SECTION_ROLES.includes(r)),
  };
}

export function useInsightsSections(): {
  reports: boolean;
  dashboards: boolean;
  /** False until /me/tenants has answered; hold section data fetches until then. */
  ready: boolean;
} {
  const { roles, isLoading } = useMyRoles();
  if (roles.length === 0) return { reports: true, dashboards: true, ready: !isLoading };
  return { ...insightsSections(roles), ready: true };
}

/** Any admin-tier role (mirrors the backend admin gates on /admin, /schedules). */
export function useIsAdmin(): boolean {
  const { roles } = useMyRoles();
  return roles.some((r) => r === 'ReportAuthor' || r === 'TenantAdmin' || r.endsWith('Admin'));
}

/**
 * CSAGE-439: an INTERNAL Canopy admin — admin-tier roles AND a root
 * (non-customer) session scope. A customer's own admin is not internal.
 * Fails CLOSED while /me/tenants is loading or on an older API that does
 * not yet return `internal` (undefined → false).
 */
export function useIsInternalAdmin(): boolean {
  const api = useApi();
  const isAdmin = useIsAdmin();
  const q = useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: () => api.fetch<MeTenantsResponse>('/me/tenants'),
    staleTime: 5 * 60 * 1000,
  });
  return isAdmin && q.data?.internal === true;
}

/**
 * CSAGE-557: whether to offer "Manage access" on a dashboard. Mirrors the
 * API's `audienceChangeScope`: an internal admin, or a customer session
 * holding DashboardAudienceManager (Canopy INSIGHTS_DASHBOARD_SHARE). Fails
 * closed while /me/tenants loads; the API still refuses what it must.
 */
export function canManageDashboardAccess(
  roles: ReadonlyArray<string>,
  internal: boolean | undefined,
): boolean {
  const admin = roles.some(
    (r) => r === 'ReportAuthor' || r === 'TenantAdmin' || r.endsWith('Admin'),
  );
  if (internal === true) return admin;
  return internal === false && roles.includes('DashboardAudienceManager');
}

export function useCanManageDashboardAccess(): boolean {
  const api = useApi();
  const { roles } = useMyRoles();
  const q = useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: () => api.fetch<MeTenantsResponse>('/me/tenants'),
    staleTime: 5 * 60 * 1000,
  });
  return canManageDashboardAccess(roles, q.data?.internal);
}
